/**
 * The standings (2026-09-08).
 *
 * User: *"We dont have leaderboards yet, we need it. For each game mode there
 * is individual leaderboards. Level, win rate, score for each player. Also in
 * leaderboards we can show nickname and image of character (with cosmetics if
 * there is)."*
 *
 * ## Why the row leads with a face
 *
 * Every other screen in this game shows you your own delver — the stage in the
 * alcove, the paperdoll, the loadout strip. A leaderboard of usernames and
 * numbers would be the one screen where the people are rows in a table, and it
 * is the screen where they are most obviously *people*. So the portrait is the
 * first thing on the row and it is the largest thing on it, and it is the skin
 * they were wearing on the run that earned the place, which is the only reason
 * anybody buys one.
 *
 * ## Why three sorts and not three lists
 *
 * The ask names three numbers — level, win rate, score. They are not three
 * leaderboards, they are three questions about the same field of players
 * ("who is furthest along", "who is most reliable", "who had the best night"),
 * and a player wants to compare their answers. One board, three orderings, all
 * three numbers on every row whichever is chosen: switching sorts should move
 * the rows, not change what a row tells you.
 *
 * Pure string builders, same as the rest of `ui/`.
 */

import type {
  LeaderboardBoard, LeaderboardEntryRow, LeaderboardModeRow, LeaderboardSort, PersonalStandingRow,
} from '../net/leaderboards-api';
import { classPortraitSrc } from './class-picker';
import { cosmeticIconSrc, esc } from './item-icon';
import type { CharacterClassId } from '../config/character-roster';
import { t, N_ } from '../i18n/i18n';

export const SORT_LABEL: Record<LeaderboardSort, string> = {
  score: N_('Score'),
  winRate: N_('Win rate'),
  level: N_('Level'),
};

/** The standing sentence per sort — whole sentences, so word order is the translator's. */
const STANDING: Record<LeaderboardSort, string> = {
  score: N_('You are {rank} of {total} by score.'),
  winRate: N_('You are {rank} of {total} by win rate.'),
  level: N_('You are {rank} of {total} by level.'),
};

const SORTS: readonly LeaderboardSort[] = ['score', 'winRate', 'level'];

/**
 * What a player looks like, with the skin if they had one on.
 *
 * The skin's asset key *is* its catalogue id (`cosmetic-defs.ts` — every SKIN
 * sets `skinAssetKey` to its own id, and a backend test holds it there), which
 * is why one string can find the plate. Falls back to the class portrait,
 * which is what '' means on the wire and what every delver without a skin
 * looks like anyway.
 */
export function delverPortraitSrc(characterClass: string, skinAssetKey: string): string {
  return skinAssetKey
    ? cosmeticIconSrc(skinAssetKey)
    : classPortraitSrc(characterClass as CharacterClassId);
}

/**
 * A percentage, and never a misleading one.
 *
 * Rounded to a whole number, because a leaderboard is read at a glance and
 * 66.7% is not a more useful fact than 67% about somebody else. A player below
 * the ranking floor still gets their real figure — the floor decides who is
 * *ranked* by it, not who is told it.
 */
export function winRateLabel(row: { winRate: number; matches: number }): string {
  if (row.matches === 0) return '—';
  return `${Math.round(row.winRate * 100)}%`;
}

/** "7W 3L" — and the draws only when a mode can actually draw one. */
export function recordLabel(row: { wins: number; losses: number; draws: number }): string {
  return row.draws > 0
    ? t('{w}W {l}L {d}D', { w: row.wins, l: row.losses, d: row.draws })
    : t('{w}W {l}L', { w: row.wins, l: row.losses });
}

export function scoreLabel(row: LeaderboardEntryRow, mode: LeaderboardModeRow): string {
  return mode.scoreUnit ? t('{score} {unit}', { score: row.bestScore, unit: t(mode.scoreUnit) }) : String(row.bestScore);
}

/**
 * The medal, for the top three only.
 *
 * A number is what a leaderboard is; the top three are the part anybody
 * remembers, and giving them a colour is the cheapest way to make the head of
 * the table read as the head of the table on a phone screen.
 */
function rankToneClass(rank: number): string {
  /**
   * One champion, two runners-up, then the field.
   *
   * The first pass gave three descending golds and it read backwards on
   * screen: `gold-bright` is a paler colour than `gold`, so second place drew
   * the eye harder than first. Saturation is the ladder here, not lightness —
   * gold for the top, cream for the two under it, dim for everyone else. No
   * new token; this screen is not the place to invent a fourth metal.
   */
  if (rank === 1) return 'text-gold';
  if (rank <= 3) return 'text-text';
  return 'text-text-dim';
}

export function sortTabsMarkup(active: LeaderboardSort): string {
  const tabs = SORTS.map((sort) => {
    const on = sort === active;
    const cls = on
      ? 'btn btn-stone-on shrink-0 px-2.5 font-heading tracking-[0.06em]'
      : 'btn btn-stone shrink-0 px-2.5 font-heading tracking-[0.06em]';
    return `<button data-lb-sort="${sort}" class="${cls}" aria-selected="${on}" role="tab">${esc(t(SORT_LABEL[sort]))}</button>`;
  }).join('');
  return `<div role="tablist" class="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1">${tabs}</div>`;
}

export function modeTabsMarkup(modes: readonly LeaderboardModeRow[], active: string): string {
  const tabs = modes.map((mode) => {
    const on = mode.mode === active;
    const cls = on
      ? 'btn btn-stone-on shrink-0 px-3 font-heading tracking-[0.06em]'
      : 'btn btn-stone shrink-0 px-3 font-heading tracking-[0.06em]';
    return `<button data-lb-mode="${esc(mode.mode)}" class="${cls}" aria-selected="${on}" role="tab">${esc(t(mode.name))}</button>`;
  }).join('');
  return `<div role="tablist" class="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1">${tabs}</div>`;
}

/**
 * One player.
 *
 * The layout is one flex row that survives 320px: rank and portrait are fixed,
 * the name column is the one that shrinks (`min-w-0` plus `truncate`, without
 * which a long username pushes the score off the right edge instead of
 * ellipsing), and the score is fixed and right-aligned so the numbers form a
 * column the eye can run down.
 *
 * Whichever sort is active, all three numbers are on the row. The active one
 * is the one in gold — that is the whole feedback for "this is why they are
 * here", and it costs nothing.
 */
export function entryRowMarkup(
  row: LeaderboardEntryRow, mode: LeaderboardModeRow, sort: LeaderboardSort, isMe: boolean,
): string {
  const name = row.username ?? t('a delver');
  const frame = isMe ? 'frame-inset bg-gold/10' : 'frame-inset';
  const gold = (on: boolean) => (on ? 'text-gold' : 'text-text-dim');
  return `
    <li class="${frame} flex items-center gap-2.5 sm:gap-3 p-2 sm:p-2.5">
      <span class="w-7 sm:w-8 shrink-0 text-center font-heading tabular-nums ${rankToneClass(row.rank)}">${row.rank}</span>
      <img src="${esc(delverPortraitSrc(row.characterClass, row.skinAssetKey))}" alt=""
        loading="lazy" width="44" height="44"
        class="w-11 h-11 shrink-0 rounded object-cover object-top bg-black/30" />
      <span class="min-w-0 flex-1">
        <span class="block truncate text-[14px] leading-tight">${esc(name)}${isMe ? ` <span class="chip text-[10px] py-0.5">${esc(t('you'))}</span>` : ''}</span>
        <span class="block truncate text-[11px] text-text-dim tabular-nums">
          <span class="${gold(sort === 'level')}">${esc(t('Lv {n}', { n: row.characterLevel }))}</span>
          &middot; <span class="${gold(sort === 'winRate')}">${esc(winRateLabel(row))}</span>
          <span class="opacity-70">(${esc(recordLabel(row))})</span>
        </span>
      </span>
      <span class="shrink-0 text-right">
        <span class="block font-heading tabular-nums text-[15px] ${sort === 'score' ? 'text-gold' : 'text-text'}">${esc(scoreLabel(row, mode))}</span>
        <span class="block text-[10px] text-text-dim">${esc(t(mode.scoreLabel))}</span>
      </span>
    </li>`;
}

/**
 * Where you stand, said even when you are not on the page.
 *
 * A board you are not on is a board with nothing on it for you, and the
 * win-rate one deliberately keeps newcomers off — so the unranked case is not
 * an empty state, it is an instruction with a number in it.
 */
export function standingMarkup(me: PersonalStandingRow, sort: LeaderboardSort, minMatches: number): string {
  if (!me.aggregate || me.aggregate.matches === 0) {
    return `<p class="text-[12px] text-text-dim">${esc(t('You have not finished a match in this mode yet. Play one and you are on the board.'))}</p>`;
  }
  if (me.rank === null) {
    const left = me.matchesUntilRanked;
    return `<p class="text-[12px] text-text-dim">${esc(t('Ranked by win rate after {n} matches — {left} more to go. You are on the Score and Level boards now.', { n: minMatches, left }))}</p>`;
  }
  return `<p class="text-[12px] text-text-dim">${t(STANDING[sort], {
    rank: `<span class="text-gold font-heading tabular-nums">#${me.rank}</span>`,
    total: me.total,
  })}</p>`;
}

export function boardMarkup(board: LeaderboardBoard, myUserId: string | null, minMatches: number): string {
  if (board.rows.length === 0) {
    const empty = board.sort === 'winRate'
      ? t('Nobody has played {n} matches of this mode yet, so there is nothing to rank by win rate. Try Score.', { n: minMatches })
      : t('Nobody has finished a match in this mode yet. The first one on the board could be yours.');
    return `<p class="text-[13px] text-text-dim">${esc(empty)}</p>`;
  }
  const rows = board.rows.map((r) => entryRowMarkup(r, board.mode, board.sort, r.userId === myUserId)).join('');
  return `
    <ol class="flex flex-col gap-1.5">${rows}</ol>
    <p class="text-[11px] text-text-dim leading-snug mt-3">${esc(t(board.mode.winMeans))}</p>`;
}
